import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VisitorCounterService {
  private visitorCountSubject = new BehaviorSubject<number>(0);
  public visitorCount$ = this.visitorCountSubject.asObservable();

  private readonly VISITOR_COUNT_KEY = 'visitor_count';
  private readonly VISITOR_SESSION_KEY = 'visitor_session';
  private readonly VISITOR_LAST_UPDATE_KEY = 'visitor_last_update';
  private readonly SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

  constructor(private http: HttpClient) {
    this.initializeCounter();
  }

  private initializeCounter(): void {
    // Get initial count from localStorage or API
    const savedCount = localStorage.getItem(this.VISITOR_COUNT_KEY);
    const count = savedCount ? parseInt(savedCount, 10) : 0;
    this.visitorCountSubject.next(count);
  }

  incrementVisitorCount(): void {
    const currentCount = this.visitorCountSubject.value;
    const newCount = currentCount + 1;
    
    // Update in localStorage
    localStorage.setItem(this.VISITOR_COUNT_KEY, newCount.toString());
    // Store the timestamp of the last update
    localStorage.setItem(this.VISITOR_LAST_UPDATE_KEY, new Date().toISOString());
    this.visitorCountSubject.next(newCount);
  }

  getVisitorCount(): number {
    const savedCount = localStorage.getItem(this.VISITOR_COUNT_KEY);
    return savedCount ? parseInt(savedCount, 10) : 0;
  }

  getLastUpdateTime(): string {
    const lastUpdate = localStorage.getItem(this.VISITOR_LAST_UPDATE_KEY);
    return lastUpdate || new Date().toISOString();
  }

  // Check if this is a new unique visitor session
  isNewVisitor(): boolean {
    const sessionData = localStorage.getItem(this.VISITOR_SESSION_KEY);
    const now = new Date().getTime();
    
    if (sessionData) {
      const session = JSON.parse(sessionData);
      // If last visit was more than 30 minutes ago, consider it a new visitor
      if (now - session.timestamp > this.SESSION_DURATION) {
        // Update the session timestamp
        localStorage.setItem(this.VISITOR_SESSION_KEY, JSON.stringify({
          timestamp: now
        }));
        return true;
      }
      return false;
    } else {
      // First visit in this session
      localStorage.setItem(this.VISITOR_SESSION_KEY, JSON.stringify({
        timestamp: now
      }));
      return true;
    }
  }

  updateVisitorRecord(): void {
    if (this.isNewVisitor()) {
      this.incrementVisitorCount();
    }
  }
}